import { useState, useEffect, useCallback } from 'react';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useBypass } from '../contexts/BypassContext';

const usePremiumAccess = (tripId) => {
  const { user } = useAuth();
  const { bypassEnabled, isDevelopment } = useBypass();
  const [hasAccess, setHasAccess] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const syncPendingPayments = useCallback(async () => {
    if (!tripId) {
      return { fixed: 0 };
    }

    try {
      const verifyResponse = await api.post(`/payments/verify-pending/${tripId}`);
      return verifyResponse.data;
    } catch (verifyErr) {
      console.error('Error verifying pending payments:', verifyErr);
      return { fixed: 0 };
    }
  }, [tripId]);

  const checkAccess = useCallback(async (options = {}) => {
    const { syncPaymentsFirst = false } = options;

    if (!user || !tripId) {
      setHasAccess(true);
      setLoading(false);
      return;
    }
    
    // If bypass is enabled in development, grant access immediately
    if (isDevelopment && bypassEnabled) {
      console.log('🚫 Bypassing premium access check - development mode');
      setHasAccess(true);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      if (syncPaymentsFirst) {
        const verifyResult = await syncPendingPayments();
        if (verifyResult.fixed > 0) {
          console.log(`✅ Synced ${verifyResult.fixed} paid checkout session(s) for trip ${tripId}`);
        }
      }

      const response = await api.get(`/payments/check-access/${tripId}`);
      const hasPremiumAccess = response.data.hasPremiumAccess;
      setHasAccess(hasPremiumAccess);

      // If access still blocked, try syncing once more (covers webhook delay edge cases)
      if (!hasPremiumAccess && !syncPaymentsFirst) {
        const verifyResult = await syncPendingPayments();
        if (verifyResult.fixed > 0) {
          const retryResponse = await api.get(`/payments/check-access/${tripId}`);
          setHasAccess(retryResponse.data.hasPremiumAccess);
        }
      }
    } catch (err) {
      console.error('Error checking premium access:', err);
      setError(err.response?.data?.message || 'Failed to check premium access');
      setHasAccess(true);
    } finally {
      setLoading(false);
    }
  }, [user, tripId, bypassEnabled, isDevelopment, syncPendingPayments]);

  useEffect(() => {
    const returningFromPayment = sessionStorage.getItem(`payment_completed_trip_${tripId}`) === 'true';
    if (returningFromPayment) {
      sessionStorage.removeItem(`payment_completed_trip_${tripId}`);
    }

    checkAccess({ syncPaymentsFirst: returningFromPayment });
  }, [checkAccess, tripId]);

  return { 
    hasAccess, 
    loading, 
    error, 
    refreshAccess: () => checkAccess({ syncPaymentsFirst: true }),
    syncPendingPayments,
  };
};

export default usePremiumAccess;
